import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoginPage } from './components/pages/LoginPage';
import { DashboardPage } from './components/pages/DashboardPage';
import { InstructorsPage } from './components/pages/InstructorsPage';
import { InstructorProfilePage } from './components/pages/InstructorProfilePage';
import { LicensesPage } from './components/pages/LicensesPage';
import { QrScansPage } from './components/pages/QrScansPage';
import { SeminarsPage } from './components/pages/SeminarsPage';
import AdminSeminarDetailPage from './components/pages/AdminSeminarDetailPage';
import RebookChoicePage from './components/pages/RebookChoicePage';
import InHouseSignupPage from './components/pages/InHouseSignupPage';
import AdminParticipantDetailPage from './components/pages/AdminParticipantDetailPage';
import ReferentsPage from './components/pages/ReferentsPage';
import NewsletterPage from './components/pages/NewsletterPage';
import AdminFaqPage from './components/pages/AdminFaqPage';
import AdminTicketsPage from './components/pages/AdminTicketsPage';
import NewExpensePage from './components/pages/NewExpensePage';
import PortalHelpPage from './components/pages/PortalHelpPage';
import { InvoicesPage } from './components/pages/InvoicesPage';
import { SettingsPage } from './components/pages/SettingsPage';
import { TemplatesPage } from './components/pages/TemplatesPage';
import TemplateEditorPage from './components/pages/TemplateEditorPage';
import { SeminarWidget } from './components/widget/SeminarWidget';
import PublicSeminarsPage from './components/pages/PublicSeminarsPage';
import SeminarDetailPage from './components/pages/SeminarDetailPage';
import ParticipantAuthPage from './components/pages/ParticipantAuthPage';
import ParticipantPortalPage from './components/pages/ParticipantPortalPage';
import ContactDetailsPage from './components/pages/ContactDetailsPage';
import MyLicenseRenewalPage from './components/pages/MyLicenseRenewalPage';
import EmailTemplatesPage from './components/pages/EmailTemplatesPage';
import LegalTextPage from './components/pages/LegalTextPage';
import PasswordResetPage from './components/pages/PasswordResetPage';
import EmailVerifyPage from './components/pages/EmailVerifyPage';
import EmailChangeConfirmPage from './components/pages/EmailChangeConfirmPage';
import ImpersonatePage from './components/pages/ImpersonatePage';
import WaitingListResponsePage from './components/pages/WaitingListResponsePage';
import PublicFaqPage from './components/pages/PublicFaqPage';
import PublicMaintenanceGate from './components/common/PublicMaintenanceGate';
import AdminCancellationsPage from './components/pages/AdminCancellationsPage';
import { CompaniesPage } from './components/pages/CompaniesPage';
import AdminAccountDeletionsPage from './components/pages/AdminAccountDeletionsPage';
import SystemHealthPage from './components/pages/SystemHealthPage';
import { useDesignColors } from './hooks/useDesignColors';
import './App.css';

// Protected Route Component
function ProtectedRoute({ children, requireAdmin = false }) {
    const { isAuthenticated, loading, user } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (requireAdmin && user?.role !== 'admin' && user?.role !== 'super_admin') {
        return <Navigate to="/portal" replace />;
    }

    // Wartungsmodus: Mitglieder-Bereich (requireAdmin=false) wird gesperrt.
    // Admins/Super-Admins werden immer durchgelassen, damit das System nie
    // ungewollt aussperrt.
    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
    if (!requireAdmin && !isAdmin) {
        return <PublicMaintenanceGate>{children}</PublicMaintenanceGate>;
    }

    return children;
}

// Public Route (redirect if already logged in)
function PublicRoute({ children }) {
    const { isAuthenticated, loading, user } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
        );
    }

    if (isAuthenticated) {
        const adminLike = user?.role === 'admin' || user?.role === 'super_admin';
        return <Navigate to={adminLike ? '/admin' : '/portal'} replace />;
    }

    return children;
}

// Public verification redirect - forwards /verify.php?token=xxx to the API
function VerifyRedirect() {
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        if (token) {
            window.location.href = `${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL || "")}/api/verify/${token}`;
        }
    }, []);
    return <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh',fontFamily:'sans-serif'}}>Weiterleitung zur Verifizierung...</div>;
}

function AppRoutes() {
    useDesignColors();
    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />

            {/* Widget Route (public, no auth) */}
            <Route path="/widget" element={<SeminarWidget />} />

            {/* Public Seminar Pages (no auth) */}
            <Route path="/seminare" element={<PublicMaintenanceGate><PublicSeminarsPage /></PublicMaintenanceGate>} />
            <Route path="/seminare/:id" element={<PublicMaintenanceGate><SeminarDetailPage /></PublicMaintenanceGate>} />

            {/* Legal Text Pages (no auth) */}
            <Route path="/nutzungsbedingungen" element={<LegalTextPage />} />
            <Route path="/datenschutz" element={<LegalTextPage />} />
            <Route path="/impressum" element={<LegalTextPage />} />

            {/* Participant Auth (no auth required, aber Wartungsmodus blockiert) */}
            <Route path="/teilnehmer/login" element={<PublicMaintenanceGate><ParticipantAuthPage /></PublicMaintenanceGate>} />
            <Route path="/teilnehmer/registrieren" element={<PublicMaintenanceGate><ParticipantAuthPage /></PublicMaintenanceGate>} />
            <Route path="/passwort-zuruecksetzen" element={<PublicMaintenanceGate><PasswordResetPage /></PublicMaintenanceGate>} />
            <Route path="/email-bestaetigen" element={<PublicMaintenanceGate><EmailVerifyPage /></PublicMaintenanceGate>} />
            <Route path="/email-change-confirm" element={<PublicMaintenanceGate><EmailChangeConfirmPage /></PublicMaintenanceGate>} />

            {/* Impersonation entry point (no auth check - token in URL) */}
            <Route path="/portal/impersonate" element={<ImpersonatePage />} />

            {/* Waiting List Token-Response (public, no auth) */}
            <Route path="/buchung/warteliste/:token" element={<WaitingListResponsePage />} />
            <Route path="/buchung/warteliste/:token/:action" element={<WaitingListResponsePage />} />
            <Route path="/umbuchung/:token" element={<RebookChoicePage />} />
            <Route path="/in-house/:token" element={<InHouseSignupPage />} />

            {/* Public FAQ / Hilfe (without auth) */}
            <Route path="/hilfe" element={<PublicFaqPage />} />

            {/* Participant Portal (auth required) */}
            <Route
                path="/portal"
                element={
                    <ProtectedRoute>
                        <ParticipantPortalPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/kontaktdaten"
                element={
                    <ProtectedRoute>
                        <ContactDetailsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/lizenzverlaengerung"
                element={
                    <ProtectedRoute>
                        <MyLicenseRenewalPage />
                    </ProtectedRoute>
                }
            />

            {/* Admin Routes */}
            <Route
                path="/admin"
                element={
                    <ProtectedRoute requireAdmin>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/instructors"
                element={
                    <ProtectedRoute requireAdmin>
                        <InstructorsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/tickets"
                element={
                    <ProtectedRoute requireAdmin>
                        <AdminTicketsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/expenses/new"
                element={
                    <ProtectedRoute requireAdmin>
                        <NewExpensePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/expenses/:expense_id/edit"
                element={
                    <ProtectedRoute requireAdmin>
                        <NewExpensePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/instructors/:id"
                element={
                    <ProtectedRoute requireAdmin>
                        <InstructorProfilePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/licenses"
                element={
                    <ProtectedRoute requireAdmin>
                        <LicensesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/qr-scans"
                element={
                    <ProtectedRoute requireAdmin>
                        <QrScansPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/seminars"
                element={
                    <ProtectedRoute requireAdmin>
                        <SeminarsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/seminars/:id"
                element={
                    <ProtectedRoute requireAdmin>
                        <AdminSeminarDetailPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/seminars/:seminarId/participants/:bookingId"
                element={
                    <ProtectedRoute requireAdmin>
                        <AdminParticipantDetailPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/referents"
                element={
                    <ProtectedRoute requireAdmin>
                        <ReferentsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/newsletter"
                element={
                    <ProtectedRoute requireAdmin>
                        <NewsletterPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/faq"
                element={
                    <ProtectedRoute requireAdmin>
                        <AdminFaqPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/portal/hilfe"
                element={
                    <ProtectedRoute>
                        <PortalHelpPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/settings/email-templates"
                element={
                    <ProtectedRoute requireAdmin>
                        <EmailTemplatesPage />
                    </ProtectedRoute>
                }
            />
            {/* Legacy redirect: /admin/email-templates → /admin/settings/email-templates */}
            <Route
                path="/admin/email-templates"
                element={<Navigate to="/admin/settings/email-templates" replace />}
            />
            <Route
                path="/admin/invoices"
                element={
                    <ProtectedRoute requireAdmin>
                        <InvoicesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/cancellations"
                element={
                    <ProtectedRoute requireAdmin>
                        <AdminCancellationsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/firmen"
                element={
                    <ProtectedRoute requireAdmin>
                        <CompaniesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/account-deletions"
                element={
                    <ProtectedRoute requireAdmin>
                        <AdminAccountDeletionsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/system-health"
                element={
                    <ProtectedRoute requireAdmin>
                        <SystemHealthPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/settings/templates"
                element={
                    <ProtectedRoute requireAdmin>
                        <TemplatesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/settings/templates/:id/edit"
                element={
                    <ProtectedRoute requireAdmin>
                        <TemplateEditorPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/settings"
                element={
                    <ProtectedRoute requireAdmin>
                        <SettingsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/settings/:tab"
                element={
                    <ProtectedRoute requireAdmin>
                        <SettingsPage />
                    </ProtectedRoute>
                }
            />

            {/* Public verification redirect - no auth required */}
            <Route path="/verify.php" element={<VerifyRedirect />} />

            {/* Redirect root to public page */}
            <Route path="/" element={<Navigate to="/seminare" replace />} />

            {/* Catch all - redirect to public page */}
            <Route path="*" element={<Navigate to="/seminare" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <Toaster
                    position="top-right"
                    toastOptions={{
                        style: {
                            background: 'white',
                            border: '1px solid hsl(var(--border))',
                        },
                    }}
                />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
